<template>
  <b-modal
    v-bind="$attrs"
    :id="$options.modalId"
    header-class="p-0"
    body-class="p-4"
    size="lg"
    @show="resetModal"
    hide-footer
    centered
  >
    <template #modal-header>
      <div class="config-modal__header-container">
        <div class="config-modal-header__title">
          {{ $t('config-title') }}
        </div>

        <i class="fal fa-times config-modal__header-close-icon" @click="$bvModal.hide($options.modalId)" />
      </div>
    </template>

    <div class="config-modal">
      <div class="config-modal__section-title">
        SEO
        <i id="seo-tooltip" class="fal fa-question-circle config-modal__pixel-icon" />
        <Tooltip target="seo-tooltip" :text="seoTooltipText" />
      </div>

      <div class="config-modal__seo">
        <div class="config-modal__seo-form">
          <div class="config-modal__input-container">
            <div data-testid="facebook-pixel-container">
              <label for="seoPageTitle">
                {{ $t('page-title') }}
              </label>
              <i id="title-tooltip" class="fal fa-question-circle config-modal__pixel-icon" />
              <Tooltip target="title-tooltip" :text="titleTooltipText" />
            </div>
            <input v-model="seoPageTitle" type="text" id="seoPageTitle" />
          </div>

          <div class="config-modal__input-container">
            <label for="seoPageDescription">
              {{ $t('page-description') }}
            </label>
            <textarea v-model="seoPageDescription" id="seoPageDescription" />
          </div>
        </div>

        <div class="config-modal__pixel-and-image-config">
          <div v-if="isFromFunnel" class="config-modal__facebook-pixel-container">
            <div data-testid="facebook-pixel-container">
              <label for="pixel-input">
                {{ $t('facebook-pixel') }}
              </label>

              <i id="pixel-tooltip" class="fal fa-question-circle config-modal__pixel-icon" />

              <Tooltip target="pixel-tooltip" :text="pixelTooltipText" />
            </div>

            <Input
              id="pixel-input"
              data-testid="facebook-pixel-input"
              :value="facebookPixelCode"
              placeholder="Insira o ID do Pixel"
              @input="fillFacebookPixel"
            />
          </div>

          <div class="config-modal__input-container">
            <input ref="imageRef" id="seoSocialMediaImage" type="file" accept="image/*" @change="handleFile" hidden />

            <label for="seoSocialMediaImage">
              {{ $t('social-media-image') }}
            </label>
            <div class="config-modal__upload-social-media-image" @click="$refs.imageRef.click()">
              <div class="config-modal__upload-social-media-title">
                {{ $t('click-for-upload') }}
              </div>

              <div class="config-modal__upload-social-media-subtitle">
                {{ $t('image-formats') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="config-modal__seo-preview-section">
        <div class="config-modal__seo-preview-google">
          <div class="config-modal__seo-preview-section-title">
            {{ $t('preview') }}
          </div>

          <div class="config-modal__seo-preview-container">
            <div class="config-modal__seo-preview-url">
              {{ pageUrl }}
            </div>

            <div class="config-modal__seo-preview-title">
              {{ seoPageTitle }}
            </div>

            <div class="config-modal__seo-preview-description">
              {{ seoPageDescription }}
            </div>
          </div>
        </div>

        <div class="config-modal__seo-preview-social-media">
          <div class="config-modal__seo-preview-section-title">
            {{ $t('social-media-preview') }}
          </div>

          <div class="config-modal__social-media-preview-container">
            <div v-if="isUploadingImage" class="config-modal__social-media-empty-image-container">
              <Spinner />
            </div>
            <div
              v-else-if="ogImageUrl && ogImageUrl.length"
              class="config-modal__social-media-image-preview"
              :style="{
                backgroundImage: `url(${ogImageUrl})`,
                backgroundSize: 'cover',
              }"
            />
            <div v-else class="config-modal__social-media-empty-image-container">
              <i class="fal fa-image fa-5x" />
            </div>

            <div class="config-modal__social-media-content-preview">
              <div class="config-modal__social-media-content-link">
                {{ pageUrl }}
              </div>

              <div class="config-modal__social-media-content-title">
                {{ seoPageTitle }}
              </div>

              <div class="config-modal__social-media-content-description">
                {{ seoPageDescription }}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="config-modal__section-title">
            {{ $t('color-pallete') }}
          </div>

          <div>
            <ColorPicker
              :color="selectedColor"
              label="Adicionar Cor"
              @submit="addColor({ color: $event || selectedColor })"
            />

            <div class="config-modal__colors-container">
              <div :id="index" v-for="(color, index) in palette" :key="color" class="config-modal__color-wrapper">
                <div class="config-modal__color-square" :style="{ backgroundColor: color }" />
                <i class="fas fa-trash-alt" @click="removeColor({ color })" />
                <Tooltip :target="`${index}`" :text="color" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Button
        :label="$t('save-button')"
        class="config-modal__save-button"
        data-testid="save-config-button"
        :button-style="{ backgroundColor: '#00E4F5', color: '#262626', border: 'none' }"
        @click="saveConfigs()"
      />
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Button from '@/shared/components/Button'
import Input from '@/shared/components/Input'
import Spinner from '@/shared/components/Spinner.vue'
import ColorPicker from '@/shared/components/ColorPicker'
import FunnelMixin from '@/components/Mixin'
import { changeJavascript } from '@/helpers/javascriptHelper.js'
import UploadFileService from '@/services/UploadFile.js'
import { errorAlert } from '@/helpers/alertHelper.js'

const howToCreateYourFacebookPixelLink =
  'https://hero-spark.zendesk.com/hc/pt-br/articles/4408183641101-Como-configurar-o-Pixel-do-Facebook-na-HeroSpark'

/*eslint-disable no-useless-escape*/
export default {
  name: 'ConfigModal',
  data() {
    return {
      pageId: this.$route.params.id,
      pageName: '',
      slug: '',
      seoPageTitle: '',
      seoPageDescription: '',
      ogImageUrl: '',
      pageUrl: '',
      selectedColor: '#FFFFFF',
      head: '',
      body: '',
      isUploadingImage: false,
      facebookPixelCode: '',
      titleTooltipText: 'Texto que irá parecer para identificar a página na aba do navegador.',
      seoTooltipText:
        'SEO serve para melhorar o posicionamento da sua página em sites de busca gerando mais conversões',
      pixelTooltipText: `Digite o ID de 14 dígitos do seu Pixel. Não sabe como? <a href="${howToCreateYourFacebookPixelLink}" target="_blank">Veja em nosso artigo os passos para conseguir o Pixel do Facebook.</a>`,
    }
  },
  components: {
    Button,
    ColorPicker,
    Input,
    Spinner,
  },
  mixins: [FunnelMixin],
  mounted() {
    this.setTitleAndDescription()
    this.setHeadData()
    if (this.$route.query.pixel) this.$bvModal.show('config-modal')
  },
  computed: {
    ...mapState('settings', ['seo', 'palette', 'javascript']),
    ...mapState('landing', ['name', 'title']),
    pageNameSlug() {
      if (!this.slug) {
        return this.name
      }
      return this.slug
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '') // Trim - from end of text
    },
  },
  methods: {
    ...mapActions('settings', ['setSEO', 'addColor', 'removeColor', 'setJavascript', 'saveSettings']),
    ...mapActions('landing', ['saveTitle', 'saveName']),
    saveConfigs() {
      this.setSEO({
        title: this.seoPageTitle,
        description: this.seoPageDescription,
        link: this.pageUrl,
        ogImageUrl: this.ogImageUrl,
      })

      this.saveJavascript()

      if (this.pageNameSlug && this.pageNameSlug.trim().length && this.pageName && this.pageName.trim().length) {
        this.saveName({ id: this.pageId, name: this.pageNameSlug })
        this.saveTitle({ id: this.pageId, title: this.pageName.trim() })
      }

      this.$bvModal.hide(this.$options.modalId)
    },
    handleFile(event) {
      this.isUploadingImage = true

      UploadFileService.uploadCore(event.target.files[0])
        .then(response => {
          const url = response.data.data.uploadAsset.url
          this.updateUrl(url)
        })
        .catch(() => {
          errorAlert(this.$t('message.image-upload-error'))
        })
        .finally(() => (this.isUploadingImage = false))
    },
    updateUrl(url) {
      this.ogImageUrl = url
    },
    setHeadData() {
      this.head = this.javascript.head
      this.body = this.javascript.body

      const matchFacebookCode = this.head.match(/fbq\(\'init\'\, \'(.*?)\'\)\;/i)

      this.facebookPixelCode = matchFacebookCode ? matchFacebookCode[1] : ''
    },
    setTitleAndDescription() {
      this.seoPageTitle = this.seo.title
      this.seoPageDescription = this.seo.description
      this.ogImageUrl = this.seo.ogImageUrl
      this.pageUrl = this.seo.link

      this.slug = this.name
      this.pageName = this.title
    },
    resetModal() {
      this.setTitleAndDescription()
      this.setHeadData()

      this.slug = this.name
      this.pageName = this.title
    },
    saveJavascript() {
      this.setJavascript({ head: this.head, body: this.body })
      this.saveSettings(this.$route.params.id)
      changeJavascript(document, this.javascript)
    },
    removeFacebookPixel() {
      const initIndex = this.head.indexOf('<!-- Facebook Pixel Code -->'),
        endString = '<!-- End Facebook Pixel Code -->',
        endIndex = this.head.indexOf(endString)

      if (initIndex > -1 && endIndex > -1) {
        this.head = `${this.head.substring(0, initIndex)}${this.head.substring(endIndex + endString.length + 1)}`
      }
    },
    onInputPageName(newTitle) {
      this.pageName = newTitle
    },
    onInputSlug(newSlug) {
      this.slug = newSlug
    },
    fillFacebookPixel(newFacebookCode) {
      this.removeFacebookPixel()

      this.facebookPixelCode = newFacebookCode

      if (this.facebookPixelCode) {
        var facebookPixel = `<!-- Facebook Pixel Code -->
        <script>
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${this.facebookPixelCode}');
          fbq('track', 'PageView');
        <\/script>
        <noscript>
          <img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=${this.facebookPixelCode}&ev=PageView&noscript=1"/>
        <\/noscript>
        <!-- End Facebook Pixel Code -->`
        this.head += facebookPixel
      }
      this.setJavascript({ head: this.head, body: this.body })
    },
  },
  i18nOptions: { keyPrefix: 'views.page-editor.config-modal' },
  modalId: 'config-modal',
}
</script>

<style lang="scss" scoped>
.config-modal__header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
}

.config-modal-header__title {
  color: $grey;
  cursor: pointer;
  padding-top: 16px;
  padding-bottom: 12px;
  font-weight: 700;
  border-bottom: 4px solid $cherry;
}

.config-modal__header-close-icon {
  cursor: pointer;
}

.config-modal {
  display: flex;
  flex-direction: column;
}

.config-modal__pixel-icon {
  margin-left: 8px;
}

.config-modal__seo {
  display: grid;
  grid-template-columns: 60% auto;
  grid-gap: 24px;
}

.config-modal__facebook-pixel-container {
  margin-top: 16px;
}

.config-modal__seo-form {
  display: flex;
  flex-direction: column;
}

.config-modal__section-title {
  font-weight: 700;
}

.config-modal__general-configs-container {
  display: flex;
  justify-content: space-between;
}

.config-modal__input-container {
  margin-top: 16px;
  display: flex;
  flex-direction: column;

  input,
  textarea {
    border: 1px solid $grey-25;
    border-radius: 2px;
    padding-left: 16px;
    padding-right: 16px;
  }

  input {
    height: 30px;
  }

  textarea {
    resize: none;
    height: 90px;
  }
}

.config-modal__upload-social-media-image {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid $grey-25;
  border-radius: 4px;
  cursor: pointer;
  height: 88px;
}

.config-modal__seo-preview-section {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 24px;
  margin-top: 24px;
}

.config-modal__social-media-preview-container {
  height: 190px;
  border: 1px solid $grey-25;
  border-radius: 4px;
  margin-top: 8px;
  width: 276px;
}

.config-modal__upload-social-media-title {
  font-weight: bold;
  font-size: 0.875rem;
}

.config-modal__upload-social-media-subtitle {
  color: $grey-25;
  font-size: 0.75rem;
}

.config-modal__social-media-content-preview {
  background: $grey-17;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 2px;
  height: 63px;
  border-radius: 4px;
}

.config-modal__social-media-content-link {
  font-size: 0.75rem;
  color: $grey-30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.config-modal__social-media-content-title {
  font-size: 0.875rem;
  color: $grey;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.config-modal__social-media-content-description {
  font-size: 0.75rem;
  color: $grey-30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.config-modal__pixel-and-image-config {
  display: flex;
  flex-direction: column;
}

.config-modal__seo-preview-google {
  display: flex;
  flex-direction: column;
}

.config-modal__seo-preview-social-media {
  display: flex;
  flex-direction: column;
}

.config-modal__social-media-image-preview {
  height: 125px;
}

.config-modal__social-media-empty-image-container {
  height: 125px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey-17;
}

.config-modal__seo-preview-section-title {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
}

.config-modal__seo-preview-container {
  height: 190px;
  border: 1px solid $grey-25;
  border-radius: 4px;
  padding: 16px;
  margin-top: 8px;
  width: 276px;
}

.config-modal__seo-preview-url {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: $grey-30;
}

.config-modal__seo-preview-title {
  font-size: 1.25rem;
  line-height: 1.5rem;
  color: #1285b9;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.config-modal__seo-preview-description {
  font-size: 0.75rem;
  color: $grey-25;
  line-height: 1.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.config-modal__save-button {
  align-self: center;
  width: fit-content;
  padding: 8px 16px;
  margin-top: 40px;
}

.config-modal__colors-container {
  display: flex;
  max-width: 350px;
  flex-wrap: wrap;
}

.config-modal__color-wrapper {
  display: flex;
  margin: 8px 8px 0 0;
}

.config-modal__color-square {
  width: 20px;
  height: 20px;
  border: 1px solid black;
  margin-right: 5px;
}
</style>
